import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  FileText,
  Plane,
} from 'lucide-react'
import { usePackage } from '../hooks/usePackage'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useFeaturedPackages } from '../hooks/useFeaturedPackages'
import { API_CONFIG } from '../config/api'
import SEO from '../components/SEO'
import { buildProductSchema, buildBreadcrumbSchema } from '../utils/schema'
import { RouteLayout, RouteSection } from '../components/RouteLayout'
import PackageCard, { PackageCardSkeleton } from '../components/PackageCard'
import BrandMark from '../components/BrandMark'
import {
  getPackageDestination,
  getPackageImageUrl,
  formatTravelDates,
  getPackageIncludesLabel,
} from '../utils/packageHelpers'
import { getWhatsAppUrl } from '../utils/whatsapp'

interface BookingFormData {
  first_name: string
  last_name: string
  email: string
  telephone: string
  message: string
  pax: string
  dates: string
  origin: string
}

export default function PackageDetail() {
  const { slug } = useParams<{ slug: string }>()
  const { packageData, loading, error } = usePackage(slug || '')
  const { franchiseData, loading: franchiseLoading } = useFranchiseData()
  const { featuredPackages, loading: relatedLoading } = useFeaturedPackages()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [bookingForm, setBookingForm] = useState<BookingFormData>({
    first_name: '',
    last_name: '',
    email: '',
    telephone: '',
    message: '',
    pax: '',
    dates: '',
    origin: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const handleBookingClick = () => {
    setShowBookingModal(true)
    setSubmitSuccess(false)
    setSubmitError('')
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setBookingForm((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    try {
      const params = new URLSearchParams({
        'lead[first_name]': bookingForm.first_name,
        'lead[last_name]': bookingForm.last_name,
        'lead[email]': bookingForm.email,
        'lead[telephone]': bookingForm.telephone,
        'lead[message]': bookingForm.message,
        'lead[franchise_id]': API_CONFIG.FRANCHISE_ID || '402',
        'lead[pax]': bookingForm.pax,
        'lead[dates]': bookingForm.dates,
        'lead[origin]': bookingForm.origin,
        'lead[package_id]': packageData?.id?.toString() || '',
      })

      const response = await fetch(`${API_CONFIG.BASE_URL}/leads?${params.toString()}`, {
        method: 'POST',
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        setSubmitSuccess(true)
        setBookingForm({
          first_name: '',
          last_name: '',
          email: '',
          telephone: '',
          message: '',
          pax: '',
          dates: '',
          origin: '',
        })
        setTimeout(() => {
          setShowBookingModal(false)
          setSubmitSuccess(false)
        }, 2000)
      } else {
        const errorData = await response.text()
        console.error('API Error:', response.status, errorData)
        throw new Error(`API Error: ${response.status}`)
      }
    } catch (submitErr) {
      setSubmitError('Failed to submit your booking request. Please try again.')
      console.error('Booking submission error:', submitErr)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getAllImages = (pkg: any) => {
    const images = []
    const coverUrl = getPackageImageUrl(pkg)
    if (coverUrl) {
      images.push({
        url: coverUrl,
        large_url: coverUrl,
        thumb_url: coverUrl,
      })
    }
    if (pkg?.photos && pkg.photos.length > 0) {
      images.push(...pkg.photos)
    }
    return images
  }

  const parseHtmlToList = (html: string) => {
    if (!html) return []
    const listItems = html.match(/<li>(.*?)<\/li>/g)
    if (!listItems) return []
    return listItems.map((item) => item.replace(/<[^>]*>/g, '').trim())
  }

  const nextImage = () => {
    if (packageData) {
      const images = getAllImages(packageData)
      setCurrentImageIndex((prev) => (prev + 1) % images.length)
    }
  }

  const prevImage = () => {
    if (packageData) {
      const images = getAllImages(packageData)
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
    }
  }

  if (loading || franchiseLoading) {
    return (
      <div className="bg-cream min-h-screen">
        <div className="page-section">
          <SEO title="Loading package..." noindex />
          <div className="animate-pulse">
            <div className="h-6 bg-line rounded w-32 mb-8" />
            <div className="h-12 bg-line rounded w-3/4 mb-4" />
            <div className="h-6 bg-line rounded w-1/2 mb-8" />
            <div className="aspect-[16/9] bg-line rounded-2xl" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !packageData) {
    return (
      <div className="bg-cream min-h-screen">
        <div className="page-section">
          <SEO title="Package not found" noindex />
          <div className="text-center">
            <h1 className="text-3xl mb-4">Package Not Found</h1>
            <p className="text-ink-soft mb-8">
              {error || 'The package you are looking for does not exist.'}
            </p>
            <Link to="/packages" className="btn-primary">
              <ArrowLeft className="h-4 w-4" />
              Back to Packages
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const images = getAllImages(packageData)
  const currentImage = images[currentImageIndex]
  const inclusions = parseHtmlToList(packageData.inclusions)
  const exclusions = parseHtmlToList(packageData.exclusions)
  const destination = getPackageDestination(packageData)
  const dates = formatTravelDates(packageData.travel_from, packageData.travel_to, packageData.dates)
  const includesLabel = getPackageIncludesLabel(packageData)
  const relatedPackages = featuredPackages
    .filter((pkg) => pkg.slug !== packageData.slug && pkg.id !== packageData.id)
    .slice(0, 3)
  const whatsappUrl = getWhatsAppUrl(
    franchiseData,
    `Hi, I would like to ask a Lifestyle Planner about ${packageData.name}.`
  )
  const inputClass =
    'w-full px-3 py-2 border border-line rounded-[10px] bg-paper text-ink focus:ring-2 focus:ring-vaia-amber focus:border-transparent outline-none'

  return (
    <div className="bg-cream min-h-screen">
      <SEO
        title={packageData.name}
        description={
          packageData.intro || packageData.description?.replace(/<[^>]*>/g, '').slice(0, 160)
        }
        canonical={typeof window !== 'undefined' ? window.location.href : undefined}
        openGraph={{
          type: 'product',
          title: packageData.name,
          description:
            packageData.intro || packageData.description?.replace(/<[^>]*>/g, '').slice(0, 160),
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          image:
            packageData.cover_photo?.large_url ||
            packageData.cover_photo?.url ||
            packageData.scraped_cover_photo ||
            undefined,
        }}
        twitter={{ card: 'summary_large_image' }}
        jsonLd={[
          buildProductSchema(
            packageData,
            typeof window !== 'undefined' ? window.location.href : ''
          ),
          buildBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Packages', url: '/packages' },
            { name: packageData.name, url: `/packages/${packageData.slug}` },
          ]),
        ]}
      />

      <RouteLayout>
        <RouteSection mark={1} className="hero !pt-[70px]" markTop={70}>
          <div className="eyebrow">
            {destination} · Package
          </div>
          <h1 className="text-[clamp(32px,5vw,48px)] leading-[1.02] m-0">{packageData.name}</h1>
          <p className="text-lg text-ink-soft max-w-[560px] mt-5 leading-relaxed">
            {packageData.intro || 'An unforgettable escape designed by your Lifestyle Planner.'}
          </p>
          <div className="flex gap-6 mt-6 flex-wrap">
            <div>
              <div className="font-mono text-[11px] text-ink-soft">DATES</div>
              <div className="font-bold">{dates}</div>
            </div>
            <div>
              <div className="font-mono text-[11px] text-ink-soft">FROM</div>
              <div className="font-bold text-vaia-amber-deep">
                {packageData.formatted_price} pp
              </div>
            </div>
            <div>
              <div className="font-mono text-[11px] text-ink-soft">INCLUDES</div>
              <div className="font-bold">{includesLabel}</div>
            </div>
          </div>
          <div className="flex gap-3.5 mt-8 flex-wrap">
            <button type="button" className="btn-primary" onClick={handleBookingClick}>
              Enquire Now
            </button>
            <a
              href={whatsappUrl}
              target={whatsappUrl.startsWith('http') ? '_blank' : undefined}
              rel={whatsappUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="btn-ghost"
            >
              WhatsApp a Planner
            </a>
          </div>
        </RouteSection>

        <RouteSection mark={2} className="!pt-0 !max-w-[820px]">
          <div className="visa-box">
            <BrandMark className="icon" />
            <div>
              <h4>Visa & entry info for {destination}</h4>
              <p>
                Requirements can change — your Lifestyle Planner confirms the latest rules for your dates.{' '}
                <Link to="/visa-support">Full visa guidance →</Link>
              </p>
            </div>
          </div>
        </RouteSection>

        <RouteSection mark={3} className="!pt-0">
          <div className="relative mb-8">
            <div
              className="aspect-[16/9] w-full rounded-[14px] flex items-center justify-center overflow-hidden border border-line bg-paper"
              style={{
                backgroundImage: currentImage
                  ? `url(${currentImage.large_url || currentImage.url})`
                  : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {!currentImage && <Plane className="h-24 w-24 text-forest opacity-40" />}
            </div>

            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-paper/90 text-forest p-2 rounded-full border border-line"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-paper/90 text-forest p-2 rounded-full border border-line"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </div>

          {images.length > 1 && (
            <div className="mb-10 flex gap-3 overflow-x-auto pb-2">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-[10px] overflow-hidden border-2 ${
                    index === currentImageIndex ? 'border-vaia-amber' : 'border-line'
                  }`}
                >
                  <img
                    src={image.thumb_url || image.url}
                    alt={`${packageData.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-[26px] mb-4">About This Package</h2>
                <div
                  className="text-ink-soft leading-relaxed prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: packageData.description }}
                />
              </div>

              {inclusions.length > 0 && (
                <div>
                  <h3 className="text-xl mb-4 flex items-center gap-2">
                    <Check className="h-5 w-5 text-vaia-green" />
                    What's Included
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {inclusions.map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Check className="h-4 w-4 text-vaia-green mt-1 flex-shrink-0" />
                        <span className="text-ink-soft">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {exclusions.length > 0 && (
                <div>
                  <h3 className="text-xl mb-4 flex items-center gap-2">
                    <X className="h-5 w-5 text-vaia-amber-deep" />
                    What's Not Included
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {exclusions.map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <X className="h-4 w-4 text-vaia-amber-deep mt-1 flex-shrink-0" />
                        <span className="text-ink-soft">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {packageData.terms && (
                <div>
                  <h3 className="text-xl mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-vaia-blue" />
                    Terms & Conditions
                  </h3>
                  <div
                    className="text-ink-soft leading-relaxed prose max-w-none bg-paper border border-line p-4 rounded-[12px]"
                    dangerouslySetInnerHTML={{ __html: packageData.terms }}
                  />
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-paper border border-line rounded-[14px] p-6">
                <div className="text-center mb-6">
                  <div className="text-3xl font-display font-bold text-vaia-amber-deep mb-1">
                    From {packageData.formatted_price}
                  </div>
                  <div className="text-sm text-ink-soft">per person sharing</div>
                </div>
                <div className="space-y-3 mb-6 text-sm">
                  <div className="flex justify-between py-2 border-b border-line">
                    <span className="text-ink-soft">Duration</span>
                    <span className="font-semibold">{packageData.duration}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-line">
                    <span className="text-ink-soft">Travel Dates</span>
                    <span className="font-semibold text-right">{dates}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-ink-soft">Destination</span>
                    <span className="font-semibold">{destination}</span>
                  </div>
                </div>
                <button type="button" className="w-full btn-primary mb-3" onClick={handleBookingClick}>
                  Enquire Now
                </button>
                <Link to="/contact" className="w-full btn-ghost block text-center">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </RouteSection>

        <RouteSection mark={4} className="!pt-0">
          <div className="sec-head">
            <h2>You may also like</h2>
            <Link to="/packages" className="sec-link">See all packages →</Link>
          </div>
          {relatedLoading ? (
            <div className="cards">
              {[1, 2, 3].map((i) => (
                <PackageCardSkeleton key={i} />
              ))}
            </div>
          ) : relatedPackages.length > 0 ? (
            <div className="cards">
              {relatedPackages.map((pkg, index) => (
                <PackageCard key={pkg.id} pkg={pkg} index={index} />
              ))}
            </div>
          ) : (
            <p className="text-ink-soft text-sm">More packages coming soon.</p>
          )}
        </RouteSection>
      </RouteLayout>

      {showBookingModal && (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50">
          <div className="bg-paper p-8 rounded-[14px] shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-line">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl m-0">Enquire Now</h2>
              <button
                onClick={() => setShowBookingModal(false)}
                className="text-ink-soft hover:text-forest"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[#FBF1DF] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-forest" />
                </div>
                <p className="text-forest text-lg font-medium">
                  Your enquiry has been sent successfully!
                </p>
                <p className="text-ink-soft mt-2">We'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleBookingSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-forest mb-1">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={bookingForm.first_name}
                      onChange={handleInputChange}
                      className={inputClass}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-forest mb-1">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={bookingForm.last_name}
                      onChange={handleInputChange}
                      className={inputClass}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-forest mb-1">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={bookingForm.email}
                    onChange={handleInputChange}
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-forest mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    name="telephone"
                    value={bookingForm.telephone}
                    onChange={handleInputChange}
                    className={inputClass}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-forest mb-1">Number of People *</label>
                    <input
                      type="text"
                      name="pax"
                      value={bookingForm.pax}
                      onChange={handleInputChange}
                      placeholder="e.g., 2 adults"
                      className={inputClass}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-forest mb-1">Travel Dates *</label>
                    <input
                      type="text"
                      name="dates"
                      value={bookingForm.dates}
                      onChange={handleInputChange}
                      placeholder="e.g., Dec 2026"
                      className={inputClass}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-forest mb-1">Departure City *</label>
                  <input
                    type="text"
                    name="origin"
                    value={bookingForm.origin}
                    onChange={handleInputChange}
                    placeholder="e.g., Johannesburg"
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-forest mb-1">Additional Message</label>
                  <textarea
                    name="message"
                    value={bookingForm.message}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Any special requests or questions..."
                    className={`${inputClass} resize-none`}
                  />
                </div>

                {submitError && (
                  <div className="bg-[#FBF1DF] border border-vaia-amber rounded-[10px] p-3">
                    <p className="text-vaia-amber-deep text-sm">{submitError}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowBookingModal(false)}
                    className="flex-1 btn-ghost"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
